// components/library/ChaptersPanelByType.jsx
import React, { Component } from "react";
import ChapterActionsMenu from "./ChapterActionsMenu";
import SummaryBadge from "./SummaryBadge";
import SummaryPanelContainer from "./SummaryPanelContainer";
import SubmissionsBadge from "./SubmissionsBadge";
import ChapterSubmissionsModal from "./ChapterSubmissionsModal";
import MarkdownPreview from "../../../components/MarkdownPreview";
import { stripHtml } from "../../../../utils";

const UNASSIGNED_KEY = "__UNASSIGNED__";

export default class ChaptersPanelByType extends Component {
  state = {
    draggingChapterId: null,
    dragOverActKey: null,
    dropTargetId: null,
    dropPos: null,
    activeActKey: null,
    showSummaryModal: false,
    selectedChapter: null,
    selectedSummaryId: null,
    submissionsModalChapter: null,
  };

  componentDidMount() {
    const projectId = this.props.project?.id || 'default';
    const storageKey = `project-active-act:${projectId}`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) this.setState({ activeActKey: stored });
    } catch {}
  }  

  /* ------------ collapse helpers ------------ */
  normKey = (actKey) => (actKey ?? UNASSIGNED_KEY);
  isActCollapsed = (actKey) => this.state.activeActKey !== this.normKey(actKey);
  toggleAct = (actKey) => {
    const key = this.normKey(actKey);
    this.setState(
      (prev) => ({ activeActKey: prev.activeActKey === key ? null : key }),
      () => {
        const projectId = this.props.project?.id || 'default';
        const storageKey = `project-active-act:${projectId}`;
        try {
          if (this.state.activeActKey !== null) {
            localStorage.setItem(storageKey, this.state.activeActKey);
          } else {
            localStorage.removeItem(storageKey);
          }
        } catch {}
      }
    );
  };

  /* ------------ actions (honor readOnly) ------------ */
  openChapter = (chapterId) => {
    const { readOnly, navigate, project } = this.props;
    if (readOnly) {
      alert("This project is archived. Restore it first to open chapters.");
      return;
    }
    const projectId = project?.id;
    if (!navigate || !projectId) return;
    navigate(`/project/${projectId}/chapter/${chapterId}`);
  };

  handleSummaryClick = async (chapter, summaryInfo, e) => {
    e.stopPropagation();
    
    // Always open the modal - generation happens inside via "Restart" button
    this.setState({
      showSummaryModal: true,
      selectedChapter: chapter,
      selectedSummaryId: summaryInfo.summaryId || null,
    });
  };

  closeSummaryModal = () => {
    this.setState({
      showSummaryModal: false,
      selectedChapter: null,
      selectedSummaryId: null,
    });
  };

  openSubmissionsModal = (chapter, e) => {
    e?.stopPropagation?.();
    this.setState({ submissionsModalChapter: chapter });
  };

  closeSubmissionsModal = () => {
    this.setState({ submissionsModalChapter: null });
  };

  /* ------------ helpers ------------ */
  getActs(structure) {
    const acts = Array.isArray(structure?.acts) ? structure.acts : [];
    return acts
      .filter((a) => a && a.key)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  sortChapters(chapters) {
    const sorted = [...(chapters || [])].sort((a, b) => {
      const ao = a.order_no ?? 0;
      const bo = b.order_no ?? 0;
      if (ao !== bo) return ao - bo;
      const at = new Date(a.created_at).getTime() || 0;
      const bt = new Date(b.created_at).getTime() || 0;
      return at - bt;
    });
    return sorted;
  }

  groupByAct(chapters, acts) {
    const map = new Map(acts.map((a) => [a.key, { act: a, chapters: [] }]));
    const unassigned = { act: null, chapters: [] };

    const sorted = this.sortChapters(chapters);
    for (const c of sorted) {
      const key = c.act_key || c.actKey || null;
      const bucket = (key && map.get(key)) || null;
      (bucket ? bucket.chapters : unassigned.chapters).push(c);
    }

    const groups = [...map.values()].sort(
      (g1, g2) => (g1.act?.order ?? 0) - (g2.act?.order ?? 0)
    );
    return unassigned.chapters.length ? [unassigned, ...groups] : groups;
  }

  computeDisplayNumber({ chapter }) {
    return chapter?.chapter_number;
  }

  /* ------------ DnD: chapter rows (honor readOnly) ------------ */
  onDragStartChapter = (e, chapter) => {
    if (this.props.readOnly) return; // block drag
    e.dataTransfer.setData("text/plain", chapter.id);
    this.setState({ draggingChapterId: chapter.id });
  };
  onDragEndChapter = () => {
    this.setState({ draggingChapterId: null, dragOverActKey: null, dropTargetId: null, dropPos: null });
  };
  onChapterDragOver = (e, actKey, chapterId) => {
    if (this.props.readOnly) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = e.clientY - rect.top < rect.height / 2 ? "before" : "after";
    this.setState({ dragOverActKey: actKey ?? null, dropTargetId: chapterId, dropPos: pos });
  };
  onChapterDrop = async (e, actKey, chapterId) => {
    if (this.props.readOnly) return;
    e.preventDefault();
    const draggedId = this.state.draggingChapterId || e.dataTransfer.getData("text/plain");
    const { dropPos } = this.state;
    this.setState({ dragOverActKey: null, dropTargetId: null, dropPos: null });
    if (!draggedId || !dropPos || draggedId === chapterId) return;
    try {
      await this.props.onMoveChapter?.({
        chapterId: draggedId,
        targetActKey: actKey ?? null,
        position: dropPos,
        targetChapterId: chapterId,
      });
    } catch (err) {
      alert(`Move failed: ${err?.message || err}`);
    } finally {
      this.onDragEndChapter();
    }
  };

  /* ------------ DnD: act header (honor readOnly) ------------ */
  onActDragOver = (e, actKey) => {
    if (this.props.readOnly) return;
    e.preventDefault();
    this.setState({ dragOverActKey: actKey ?? null, dropTargetId: null, dropPos: null });
  };
  onActDrop = async (e, actKey) => {
    if (this.props.readOnly) return;
    e.preventDefault();
    const draggedId = this.state.draggingChapterId || e.dataTransfer.getData("text/plain");
    this.setState({ dragOverActKey: null, dropTargetId: null, dropPos: null });
    if (!draggedId) return;
    try {
      await this.props.onMoveChapter?.({
        chapterId: draggedId,
        targetActKey: actKey ?? null,
        position: "end",
        targetChapterId: null,
      });
    } catch (err) {
      alert(`Move failed: ${err?.message || err}`);
    } finally {
      this.onDragEndChapter();
    }
  };

  /* ------------ row renderer (kebab outside <li>) ------------ */
  renderRow = (c, idxGlobal, numberingMode, idxInAct, isChild = false, actKey = null, dropState) => {
    const { readOnly, onArchiveChapter, onSetChapterStatus, summariesMap = {}, submissionsCountByChapter = {} } = this.props;
    const { dropTargetId, dropPos } = dropState || {};
    const { summaryLoading, selectedChapter } = this.state;
    const displayNo = this.computeDisplayNumber({ chapter: c, idxGlobal, idxInAct, numberingMode });

    const dropClass =
      c.id === dropTargetId ? (dropPos === "before" ? " drop-before" : " drop-after") : "";

    const summaryInfo = summariesMap[c.id] || {};
    const chapterStatus = c.status || (c.archived_at ? "archived" : "active");
    
    // Check if there's at least one summary available in the entire map
    const hasAnySummary = Object.values(summariesMap).some(
      s => s && (s.success || s.summaryId)
    );
    
    // Disable badge if no summaries exist anywhere
    const isBadgeDisabled = !hasAnySummary && !summaryInfo.success && !summaryInfo.summaryId;

    return (
      <div className="chapter-row-wrap" key={c.id} style={{ position: "relative" }}>
        
        <li
          className={`chapter-row${isChild ? " is-child" : ""}${dropClass}${readOnly ? " is-locked" : ""}`}
          draggable={!readOnly}
          onClick={() =>
            readOnly
              ? alert("This project is archived. Restore it first to open chapters.")
              : this.openChapter(c.id)
          }
          onDragStart={(e) => this.onDragStartChapter(e, c)}
          onDragEnd={this.onDragEndChapter}
          onDragOver={(e) => this.onChapterDragOver(e, actKey, c.id)}
          onDrop={(e) => this.onChapterDrop(e, actKey, c.id)}
          aria-disabled={readOnly || undefined}
          title={readOnly ? "Restore to open or move" : undefined}
        >
          <div className="chapter-index">{displayNo}</div>
          <div className="chapter-main">
            <div className="chapter-title-row">
              <span className="chapter-title-text">{c.title}</span>
              <div className="chapter-title-badges">
                <SummaryBadge
                  onClick={(e) => this.handleSummaryClick(c, summaryInfo, e)}
                  model={summaryInfo.model}
                  provider={summaryInfo.provider}
                  loading={summaryInfo.status === 'loading'}
                  status={summaryInfo.status}
                  error={summaryInfo.error}
                  disabled={isBadgeDisabled}
                />
                <SubmissionsBadge
                  count={submissionsCountByChapter[c.id] || 0}
                  onClick={(e) => this.openSubmissionsModal(c, e)}
                />
                <span className="chapter-meta">
                  {new Date(c.updated_at || c.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            {c.content && (
              <div className="chapter-excerpt">
                <MarkdownPreview content={c.content} maxLength={120} />
              </div>
            )}
          </div>
        </li>

        <div
          className="row-kebab-anchor"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            height: "100%",
            display: "flex",
            alignItems: "center",
            paddingRight: 8,
            // keep it above the li so clicks don't hit li:
            zIndex: 2,
            // optional: pull slightly outside li’s padding if needed
            pointerEvents: "auto",
          }}
        >
          <ChapterActionsMenu
            placement="row"
            chapter={c}
            items={chapterStatus === "archived"
              ? [{
                  key: "restore",
                  label: "🔄 Restore to Active",
                  onClick: () => onSetChapterStatus?.(c.id, "active"),
                }]
              : [{
                  key: "draft",
                  label: "📝 Move to Draft",
                  onClick: () => onSetChapterStatus?.(c.id, "draft"),
                }, {
                  key: "archive",
                  label: "🗄 Archive",
                  onClick: () => onArchiveChapter?.(c.id),
                }]}
            readOnly={readOnly}            
          />
        </div>
      </div>
    );
  };

  render() {
    const { project, loading, error, chapters, projectType, readOnly, summariesMap, submissionsCountByChapter = {}, onReloadSubmissions } = this.props;
    const { dragOverActKey, dropTargetId, dropPos, showSummaryModal, selectedChapter, selectedSummaryId, submissionsModalChapter } = this.state;
    if (!project) return null;

    const structure = projectType?.structure || {};
    const chapterListCfg = structure.chapterList || {};
    const numberingMode = chapterListCfg.numbering || "global";
    const showActHeaders = chapterListCfg.showActHeaders !== false;

    const acts = this.getActs(structure);
    const grouped = this.groupByAct(chapters, acts);
    const allChaptersInOrder = grouped.flatMap((g) => g.chapters);

    return (
      <>
        {/* Summary Modal */}
        <SummaryPanelContainer
          isOpen={showSummaryModal}
          onClose={this.closeSummaryModal}
          chapter={selectedChapter}
          summaryId={selectedSummaryId}
          projectId={project.id}
        />

        {/* Submissions inbox modal */}
        <ChapterSubmissionsModal
          isOpen={!!submissionsModalChapter}
          chapter={submissionsModalChapter}
          onClose={this.closeSubmissionsModal}
          onReviewed={onReloadSubmissions}
        />

        {loading && (
          <ul className="chapters-list">
            {[...Array(3)].map((_, i) => (
              <li key={i} className="chapter-row is-skeleton">
                <div className="chapter-index">{i + 1}</div>
                <div className="chapter-main">
                  <div className="chapter-title">Loading…</div>
                  <div className="chapter-meta">Please wait</div>
                </div>
                <div className="chapter-row-actions">
                  <button className="btn subtle" disabled>
                    Open
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {error && !loading && <div className="projects-error">Could not load chapters: {error}</div>}

        {!loading && !error && (
          <ul
            className={`chapters-list${readOnly ? " read-only" : ""}`}
            aria-disabled={readOnly || undefined}
            data-dnd-disabled={readOnly ? "true" : "false"}
            style={{ position: "relative" }}
          >
            {chapters.length === 0 ? (
              <li className="chapter-row is-static">
                <div className="chapter-index">–</div>
                <div className="chapter-main">
                  <div className="chapter-title">No chapters yet</div>
                  <div className="chapter-meta">
                    {readOnly ? "Restore this project to add chapters" : "Create one to get started"}
                  </div>
                </div>
              </li>
            ) : (
              grouped.map((group) => {
                const actKey = group.act?.key ?? null;
                const firstId = group.chapters[0]?.id;
                const startGlobalIndex =
                  numberingMode === "global"
                    ? Math.max(0, allChaptersInOrder.findIndex((c) => c.id === firstId))
                    : 0;

                const collapsed = this.isActCollapsed(actKey);

                const actHeaderClasses =
                  "chapter-row act-row is-static" +
                  (dragOverActKey === actKey && !readOnly ? " drop-over" : "") +
                  (collapsed ? " is-collapsed" : "");

                const actTitle = group.act?.name || "Unassigned";
                const percent = group.act?.percentRange;

                return (
                  <React.Fragment key={group.act?.key || "unassigned"}>
                    {showActHeaders && (
                      <li
                        className={actHeaderClasses}
                        onDragOver={(e) => this.onActDragOver(e, actKey)}
                        onDrop={(e) => this.onActDrop(e, actKey)}
                        aria-expanded={!collapsed}
                        aria-disabled={readOnly || undefined}
                        title={readOnly ? "Restore to reorganize" : undefined}
                      >
                        <div className="chapter-index">
                          <button
                            type="button"
                            className="act-toggle-index"
                            onClick={(e) => {
                              e.stopPropagation();
                              this.toggleAct(actKey);
                            }}
                            aria-label={collapsed ? `Expand ${actTitle}` : `Collapse ${actTitle}`}
                            aria-expanded={!collapsed}
                          >
                            <span className="chevron" aria-hidden>
                              {collapsed ? "▶" : "▼"}
                            </span>
                          </button>
                        </div>

                        <div className="chapter-main">
                          <div className="chapter-title act-title">{actTitle}</div>
                          {percent && (
                            <div className="chapter-meta act-meta">
                              {percent[0]}%–{percent[1]}%
                            </div>
                          )}
                        </div>

                        <div className="chapter-row-actions" />
                      </li>
                    )}

                    {!collapsed &&
                      group.chapters.map((c, idxInAct) => {
                        const idxGlobal =
                          numberingMode === "global" ? startGlobalIndex + idxInAct : idxInAct;
                        return this.renderRow(
                          c,
                          idxGlobal,
                          numberingMode,
                          idxInAct,
                          true,
                          actKey,
                          { dropTargetId, dropPos }
                        );
                      })}
                  </React.Fragment>
                );
              })
            )}
          </ul>
        )}
      </>
    );
  }
}
